import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {GenericModal} from '@techsee/techsee-ui-common/lib/modals/generic';
import {ITab, SimpleTabs} from '@techsee/techsee-ui-common/lib/tabs/simple';
import Translate from '../../components/translate/Translate';
import {ISearchedResource, SearchResultsDialogController} from './SearchResultDialog.controller';
import './SearchResultDialog.style.scss';

interface Props {
    ctrl: SearchResultsDialogController;
}

@observer
export class SearchResultsDialog extends TechseeBaseComponent<Props> {
    renderInternal(): JSX.Element {
        const {ctrl} = this.props;
        if (!ctrl.isVisible) return <div />;

        return (
            <GenericModal
                className='dashboard-search-results-modal'
                show={ctrl.isVisible}
                isCloseVisible={false}
                onHideHandler={ctrl.close}
                header={() => this.renderHeader()}
                content={() => this.renderContent()}
                toolbar={() => null}
            />
        );
    }

    private renderHeader(): JSX.Element {
        const {ctrl} = this.props;

        return (
            <div className='modal-header'>
                <button className='ts-remove-btn-light pull-right' onClick={ctrl.close}>
                    &times;
                </button>

                <h4 className='modal-title'>
                    <Translate
                        text='SEARCH_TAGS.MODAL.VIEW.RESULTS'
                        translateValues={{searchTerm: ctrl.searchSettings.searchTerm}}
                    />
                </h4>
            </div>
        );
    }

    private renderContent(): JSX.Element {
        return (
            <div className='modal-body'>
                <div className='search-tabs-container'>{this.renderTabs()}</div>
            </div>
        );
    }

    private renderTabs(): JSX.Element {
        const {ctrl} = this.props;

        const tabsList: ITab[] = ctrl.tabsConfig.map((tabConfig) => ({
            title: tabConfig.title,
            content: () => (tabConfig.type === 'HISTORY' ? this.renderHistoryTab() : this.renderLibraryTab())
        }));

        return (
            <SimpleTabs
                uid='search-results-tabs'
                tabsList={tabsList}
                initialTabIndex={ctrl.initialTabIndex}
                onChange={ctrl.setActiveTab}
            />
        );
    }

    private renderHistoryTab(): JSX.Element {
        const {ctrl} = this.props;
        const history = ctrl.tagSearchResults?.history;

        if (!history || !history.keys?.length) {
            return <div>No history results</div>;
        }

        return (
            <div className='ts-search-resources-list'>
                {history.keys.map((roomId) => {
                    const resources = history[roomId] as ISearchedResource[];
                    if (!resources?.length) return null;

                    return resources
                        .slice()
                        .reverse()
                        .map((resource) => (
                            <div key={`${roomId}-${resource._id}`} className='thumbnail-container'>
                                <div
                                    className='thumbnail'
                                    onClick={() =>
                                        resource.storageType !== 'TEMP' && ctrl.selectResource(resource, roomId)
                                    }>
                                    <img src={resource.url} alt='' />
                                    {resource.storageType === 'TEMP' && (
                                        <div className='caption'>
                                            <p className='temp-caption'>
                                                <Translate
                                                    text={`HISTORY.VIEW.TEMP_${resource.isVideo ? 'VIDEO' : 'IMAGE'}`}
                                                />
                                            </p>
                                        </div>
                                    )}
                                    <div className='caption tag-caption'>
                                        <ul>
                                            {resource.tags?.map((tag: {text: string}, tagIndex: number) => (
                                                <li key={tagIndex}>{tag.text}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ));
                })}
            </div>
        );
    }

    private renderLibraryTab(): JSX.Element {
        const {ctrl} = this.props;
        const library = ctrl.tagSearchResults?.library;

        if (!library?.length) {
            return <div>No library results</div>;
        }

        return (
            <div className='ts-search-resources-list'>
                {library
                    .slice()
                    .reverse()
                    .map((resource) => (
                        <div key={resource._id} className='thumbnail-container'>
                            <div
                                className='thumbnail'
                                onClick={() => resource.storageType !== 'TEMP' && ctrl.selectResource(resource)}>
                                <img src={resource.url} alt='' />
                                {resource.storageType === 'TEMP' && (
                                    <div className='caption'>
                                        <p className='temp-caption'>
                                            <Translate
                                                text={`HISTORY.VIEW.TEMP_${resource.isVideo ? 'VIDEO' : 'IMAGE'}`}
                                            />
                                        </p>
                                    </div>
                                )}
                                <div className='caption tag-caption'>
                                    <ul>
                                        {resource.tags?.map((tag, tagIndex) => (
                                            <li key={tagIndex}>{tag.text}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    ))}
            </div>
        );
    }
}
